import { ref } from 'vue';
import type { SearchLocation } from '@/types/domain';
import {
  ensureInitialGeolocation,
  loadStoredLocation,
} from '@/helpers/geolocationHelper';

const DEFAULT_RADIUS_KM = 10;
const SEARCH_RADIUS_OPTIONS = [1, 2, 3, 10, 25, 50];

export function resolveSearchRadiusKm(
  value: number | null | undefined,
  options: number[] = SEARCH_RADIUS_OPTIONS
): number | null {
  if (value === null || value === undefined) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const closest = options.reduce((prev, curr) =>
    Math.abs(curr - numericValue) < Math.abs(prev - numericValue) ? curr : prev
  );

  return closest;
}

export function useGeolocation() {
  const location = ref<SearchLocation>({
    address: null,
    lat: null,
    lng: null,
    placeId: null,
  });
  const radiusKm = ref<number | null>(DEFAULT_RADIUS_KM);
  const isLoading = ref(false);

  const loadFromStorage = (): boolean => {
    const stored = loadStoredLocation();
    if (!stored) return false;

    location.value = {
      address: stored.formattedAddress ?? null,
      lat: stored.latitude,
      lng: stored.longitude,
      placeId: stored.placeId ?? null,
    };
    radiusKm.value =
      resolveSearchRadiusKm(stored.radiusKm) ?? DEFAULT_RADIUS_KM;
    return true;
  };

  const requestGeolocation = async (language?: string): Promise<boolean> => {
    isLoading.value = true;
    try {
      const result = await ensureInitialGeolocation(language);
      if (!result) return false;

      location.value = {
        address: result.formattedAddress ?? null,
        lat: result.latitude,
        lng: result.longitude,
        placeId: result.placeId ?? null,
      };
      radiusKm.value =
        resolveSearchRadiusKm(result.radiusKm) ?? DEFAULT_RADIUS_KM;
      return true;
    } finally {
      isLoading.value = false;
    }
  };

  const setLocation = (newLocation: Partial<SearchLocation>) => {
    location.value = {
      ...location.value,
      ...newLocation,
    };
  };

  const setRadius = (value: number | null) => {
    radiusKm.value = resolveSearchRadiusKm(value) ?? DEFAULT_RADIUS_KM;
  };

  const hasCoordinates = (): boolean => {
    return (
      typeof location.value.lat === 'number' &&
      typeof location.value.lng === 'number'
    );
  };

  return {
    location,
    radiusKm,
    isLoading,
    loadFromStorage,
    requestGeolocation,
    setLocation,
    setRadius,
    hasCoordinates,
  };
}
